import {
  Autocomplete,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip
} from "@mui/material";
import useOrganizations from "../../../hooks/UseOrganizations";
import React, {useState} from "react";
import {ModalProps} from "@mui/material/Modal";
import {useTranslation} from "react-i18next";
import styles from "./Creation.module.scss";
import {CreateApiKeyResponse, Organization} from "../../../clients/ApiKeysClient";
import {ContentCopy} from "@mui/icons-material";
import useApiKeysClient from "../../../hooks/UseApiKeysClient";
import {createFilterOptions} from "@mui/material/Autocomplete";
import {enqueueSnackbar} from "notistack";

type CreationProps = {
    open: ModalProps['open'],
    onClose: (reason: string) => void
}

export default function Creation({open, onClose}: CreationProps) {
    const {t} = useTranslation()
    const organizations = useOrganizations()
    const apiKeysClient = useApiKeysClient()
    const [selectedOrganisations, setSelectedOrganizations] = useState<Organization[]>([])
    const [createdApiKey, setCreatedApiKey] = useState<CreateApiKeyResponse | null>(null);
    const [isApiKeyCreated, setIsApiKeyCreated] = useState<boolean>(false)

    const createApiKey = async () => {
        if(selectedOrganisations.length === 0)
          return enqueueSnackbar("Please select at least one organization.", {variant: "warning"});

        apiKeysClient.createApiKey(selectedOrganisations.map(o => o.id))
            .then(apiKey => {
                setCreatedApiKey(apiKey)
                setIsApiKeyCreated(true)
            })
    }

    const handleClose = () => {
        setSelectedOrganizations([])
        setCreatedApiKey(null)
        setIsApiKeyCreated(false)
        onClose('closed')
    }

    const handleCopyToClipboard = () => {
      navigator.clipboard.writeText(createdApiKey!.apiKey);
      enqueueSnackbar("Api Key copied.", {variant: "success"});
    }

    const copyToClipboardAdornment = () => (
        <InputAdornment position="end">
            <Tooltip title={t("apikeys.create.dialog.completed.copyTooltip")} disableFocusListener>
                <IconButton autoFocus edge="end" onClick={handleCopyToClipboard}><ContentCopy /></IconButton>
            </Tooltip>
        </InputAdornment>
    )

    const chooseOrganisationsDialog = () => (
        <>
            <DialogTitle className={styles.dialog__title}>{t("apikeys.createButtonText")}</DialogTitle>
            <DialogContent>
                <DialogContentText className={styles.dialog__description}>{t("apikeys.create.dialog.text")}</DialogContentText>

                <Autocomplete
                  fullWidth
                  multiple
                  className={styles.dialog__form}
                  options={organizations}
                  value={selectedOrganisations}
                  filterOptions={createFilterOptions({limit: 100})}
                  getOptionLabel={o => `${o.name} (${o.id})`}
                  onChange={(e, v) => setSelectedOrganizations(v)}
                  renderInput={(params) => <TextField autoFocus {...params} label={t("apikeys.create.dialog.selectOrganisation.label")} />} />

                <Stack direction="row" spacing={2} justifyContent="flex-end" className={styles.dialog__buttons}>
                    <Button onClick={handleClose}>{t("apikeys.create.dialog.buttonCancel")}</Button>
                    <Button variant="contained" onClick={createApiKey}>{t("apikeys.create.dialog.buttonCreate")}</Button>
                </Stack>
            </DialogContent>
        </>
    )

    const createdDialog = () => (
        <>
            <DialogTitle className={styles.dialog__title}>{t("apikeys.create.dialog.completed.title")}</DialogTitle>
            <DialogContent>
                <TextField
                    className={styles.dialog__apiKey}
                    variant="outlined"
                    label={t("apikeys.create.dialog.completed.fieldLabel")}
                    value={createdApiKey!.apiKey}
                    InputProps={{
                        endAdornment: copyToClipboardAdornment(),
                        readOnly: true
                    }}
                    fullWidth
                />
                <Stack direction="row" spacing={2} justifyContent="space-between" className={styles.dialog__buttons}>
                    <Button onClick={handleClose}>{t("apikeys.create.dialog.completed.closeButton")}</Button>
                    <Tooltip disableFocusListener title={t("apikeys.create.dialog.completed.copyTooltip")}>
                        <Button variant="contained" onClick={() => {handleCopyToClipboard(); handleClose();}}>{t("apikeys.create.dialog.completed.copyButton")}</Button>
                    </Tooltip>
                </Stack>
            </DialogContent>
        </>
    )

    return (
        <Dialog open={open} onClose={handleClose} className={styles.dialog} fullWidth>
            {isApiKeyCreated ? createdDialog() : chooseOrganisationsDialog()}
        </Dialog>
    )
}
